import { PropsWithChildren, useCallback, useEffect, useState } from "react";
import { v4 } from "uuid";
import FilterService from "../../../services/FilterService";
import ConsolidatedService from "../../../services/ConsolidatedService";
import InvoiceService from "../../../services/InvoiceService";
import { Sort } from "../../shared/entities/Sort";
import TableContext, { TableContextValues } from "../../task/TableContext";
import { InvoiceListRequest } from "./entities/InvoiceListRequest";
import { InvoiceListResponse } from "./entities/InvoiceListResponse";

let reqId = v4();

export type InvoiceListFilters = {
  textSearch?: string;
  quickfilter?: string;
  extraFilters: string[];
  quickExtraFilter: string;
  iotree?: string;
  impid?: string;
};

export class InvoiceTableContextValues extends TableContextValues<
  InvoiceListResponse,
  InvoiceListFilters
> {
  constructor(public getFilters: () => InvoiceListRequest) {
    super();
  }
}

type PageItem = { IOID: number; pending: number; CurrencyID: number };

const InvoiceTableProvider = (
  props: PropsWithChildren<{
    clientId?: number;
    groupId?: number;
    pagesize?: number;
    extraFilters?: string;
    quickfilter?: string;
    impid?: string | null;
    selectedInvoiceIds?: number[];
    consolidatedPk?: string;
    onPageLoaded?: (items: PageItem[]) => void;
  }>
) => {
  const [response, setResponse] = useState<InvoiceListResponse>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [currentPage, _setCurrentPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(1);
  const [filters, setFilters] = useState<InvoiceListFilters>({
    quickfilter: props.quickfilter ?? "1",
    extraFilters: FilterService.ParseExtraFiltersResponseString(
      props.extraFilters
    ),
    quickExtraFilter: "",
    iotree: "",
    impid: props.impid || ""
  });
  const [sort, _setSort] = useState<Sort>({} as Sort);
  const [pagesize, setPagesize] = useState<number>(props.pagesize ?? 50);

  const getFilters = useCallback(
    (currentPage: number, filters: InvoiceListFilters): InvoiceListRequest => {
      const extraFilters = [...filters.extraFilters];
      if (filters.quickExtraFilter !== "") {
        extraFilters.push(filters.quickExtraFilter);
      }
      const request: InvoiceListRequest = {
        personid: props.clientId?.toString(),
        quickfilter: filters.quickfilter,
        page: currentPage,
        pagesize: props.selectedInvoiceIds ? 999999 : pagesize,
        sortcolumn: sort.sortColumn,
        descending: sort.sortDirection,
        filter: FilterService.GetExtraFiltersRequestString(extraFilters),
        groupid: props.groupId,
        iotree: filters.iotree,
        text: filters.textSearch,
        impid: filters.impid
      };
      if (props.selectedInvoiceIds && props.selectedInvoiceIds.length > 0) {
        request.invoiceIds = props.selectedInvoiceIds;
      }
      return request;
    },
    [
      pagesize,
      props.clientId,
      props.groupId,
      props.selectedInvoiceIds,
      sort.sortColumn,
      sort.sortDirection
    ]
  );

  const requestData = useCallback(async () => {
    setLoading(true);
    reqId = v4();
    const ourRequest = reqId;
    const request = getFilters(currentPage, filters);
    const result = props.consolidatedPk
      ? await ConsolidatedService.getConsolidatedIoList(
          request,
          props.consolidatedPk
        )
      : await InvoiceService.getList(request);
    if (ourRequest !== reqId) {
      return;
    }
    if (result instanceof Error) {
      setError(true);
      setLoading(false);
      return;
    }
    const totalPages = Math.ceil(result.itemCount / result.pageSize);
    setResponse(result);
    _setCurrentPage(result.page);
    setPageCount(totalPages);
    setLoading(false);
    //setItemCount(result.itemCount);
    setError(false);
  }, [currentPage, filters, getFilters, props.consolidatedPk]);

  useEffect(() => {
    if (props.selectedInvoiceIds !== undefined) {
      _setCurrentPage(0);
    }
  }, [props.selectedInvoiceIds]);

  useEffect(() => {
    requestData();
  }, [currentPage, sort, requestData, props.selectedInvoiceIds]);

  useEffect(() => {
    if (props.onPageLoaded && response?.list) {
      props.onPageLoaded(
        response.list.map((item) => ({
          IOID: item.IOID,
          pending: item.pending,
          CurrencyID: item.CurrencyID
        }))
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [response]);

  const setCurrentPage = (page: number) => {
    if (page >= 0 && page - 1 <= pageCount) {
      _setCurrentPage(page);
    }
  };

  const applyFilters = (newFilters: InvoiceListFilters) => {
    setCurrentPage(0);
    setFilters((x) => {
      const allSame = (
        Object.keys(newFilters) as (keyof InvoiceListFilters)[]
      ).every((k) => newFilters[k] === x[k]);
      if (allSame) return x;
      return { ...x, ...newFilters };
    });
  };

  const setSortColumn = (key: string) => {
    _setSort({
      sortColumn: key,
      sortDirection:
        sort.sortColumn === key ? (sort.sortDirection === 1 ? 0 : 1) : 0
    });
  };

  useEffect(() => {
    if (props.quickfilter === undefined) return;
    setFilters((x) => {
      if (x.quickfilter === props.quickfilter) return x;
      return { ...x, quickfilter: props.quickfilter };
    });
  }, [props.quickfilter]);

  const applySearch = (keyword: string) => {
    setFilters((x) => ({ ...x, textSearch: keyword }));
  };

  const value = new InvoiceTableContextValues(() =>
    getFilters(currentPage, filters)
  );
  value.loading = loading;
  value.error = error;
  value.response = response;
  value.currentPage = currentPage;
  value.pageSize = pagesize;
  value.setCurrentPage = setCurrentPage;
  value.pageCount = pageCount;
  value.reload = requestData;
  value.applyFilters = applyFilters;
  value.sort = sort;
  value.setSortColumn = setSortColumn;
  value.setPageSize = setPagesize;
  value.applySearch = applySearch;
  return (
    <TableContext.Provider value={value}>
      {props.children}
    </TableContext.Provider>
  );
};

export default InvoiceTableProvider;
